import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import styled from "styled-components"
import { Input } from "components/ui/inputs/Input";
import { Button } from "components/ui/controls/Button";
import { calculateWidening } from "./Widening.calculate";
import { useEffect, useState } from "react";
import { useErrors } from "providers/ErrorsProvider";
import { parseHorizontalGeometry } from "utilities/geometricRoadutilities/parseHorizontalGeometry";
import { calculationLimits } from "store/calculation.config";
import { Table } from "components/ui/display/Table";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const Widening = () => {

    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        laneWidth: '',
    });

    const [calculationInput, setCalculationInput] = useState({
        designVehicle: '',
        speedlimit: '',
        laneWidth: '',
        horizontalGeometry: null
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateWidening(calculationInput);

        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }

        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });

    }, [calculationInput, setError, unsetError]);

    const pasteHandler = async () => {
        const clipboard = await navigator.clipboard.readText();
        const parsed = parseHorizontalGeometry(clipboard);
        if(!parsed)
        {
            setError('TOOLS_HORIZONTAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, horizontalGeometry: null}));
        }
        else
        {
            unsetError('TOOLS_HORIZONTAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, horizontalGeometry: parsed}));
        }
    }

    const copyToClipboard = (content) => {
        let resultText = "";
        content.forEach(row => {
            let rowText = row.stationing.toFixed(3) + "\t" + row.extraWidth.toFixed(3) + "\t Arv";
            resultText += rowText + "\n";
        });
        
        navigator.clipboard.writeText(resultText.trim());
        setError("TOOLS_TABLE_COPIED_TO_CLIPBOARD");
    }

    return (
        <StyledWidening>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title='Inndata'>
                    <DropdownMenu
                        label="Dimensjonerende kjøretøy"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='traffic_jam' weight={300}/>}
                        defaultName='Velg dim. kjøretøy...'
                        defaultSelected={calculationInput.designVehicle}
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, designVehicle: e}))}}
                    >
                        <DropdownOption value='personbil' name='Personbil'/>
                        <DropdownOption value='litenLastebil' name='Liten lastebil'/>
                        <DropdownOption value='lastebil' name='Lastebil'/>
                        <DropdownOption value='buss' name='Buss'/>
                        <DropdownOption value='vogntog' name='Vogntog'/>
                        <DropdownOption value='modulvogntog' name='Modulvogntog'/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Kjørefeltbredde"
                        prefix={<span>b<sub>felt</sub></span>}
                        sufix='m'
                        type='number'
                        error={calculatedState.input?.laneWidth?.severity === 'error' ?? null}
                        warning={calculatedState.input?.laneWidth?.severity === 'warning' ?? null}
                        value={rawInput.laneWidth}
                        onChange={(e) => setRawInput(prev => ({...prev, laneWidth: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, laneWidth: e.target.value}))}
                    />
                    <Button
                        prefix={<MaterialIcon name='content_paste' weight={300}/>}
                        text="Lim inn horisontaldata"
                        type="secondary"
                        onClick={pasteHandler}
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                {
                    calculatedState.output?.wideningData?.valid
                    && Array.isArray(calculatedState.output?.wideningData?.value)
                    && calculatedState.output?.wideningData?.value.length !== 0 ? 
                    <ToolsContainer title="Breddeutvidelse">
                        <Table
                            headers={[
                                {key: 'stationing', label: <span>Profil</span>},
                                {key: 'extraWidth', label: <span>Bredde</span>},
                            ]}
                            data={calculatedState.output.wideningData.value}
                            errorKey={'error'}
                        />
                        <Button
                            type='secondary'
                            prefix={<MaterialIcon name='content_copy' weight={300}/>}
                            text={'Kopier til utklipstavle'}
                            onClick={() => copyToClipboard(calculatedState.output.wideningData.value)}
                        />
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.input?.horizontalGeometry?.valid ? 
                    <ToolsContainer title='Importert horisontalgeometri'>
                        <Table
                            headers={[
                                {key: 'type', label: <span>Element</span>},
                                {key: 'startStationing', label: <span>Startprofil</span>, unit: '[m]'},
                                {key: 'mergedRadius', label: <span>Radius</span>, unit: '[m]'},
                                {key: 'clothoideParameter', label: <span>Parameter</span>, unit: '[m]'},
                                {key: 'directionChange', label: <span>Retningsendring</span>, unit: '[°]'},
                                {key: 'length', label: <span>Lengde</span>, unit: '[m]'},
                            ]}
                            data={calculatedState.input.horizontalGeometry.value}
                        />
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
        </StyledWidening>
    )
}

const StyledWidening = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }
`